/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import { getSiteOverrides } from './site-overrides'

// 语言包按需异步加载:此前 7 种语言 ~2.4MB JSON 全部静态打进主包,是
// PageSpeed「未使用的 JavaScript ≈1.3MB」的最大来源。改为 i18next 自定义
// backend + 动态 import,rspack 会为每个语言 json 拆独立 chunk,首屏只拉
// 当前语言(+ en 回退),切换语言时再拉对应包。
const lazyLocaleBackend = {
  type: 'backend' as const,
  init() {},
  read(
    lng: string,
    ns: string,
    callback: (err: unknown, data?: unknown) => void
  ) {
    import(`./locales/${lng}.json`)
      .then((m) => {
        // 语言文件顶层是 { translation: {...} } 的命名空间结构(历史上被
        // resources: { zh } 恰好按 {lng:{ns:data}} 消化);这里取出对应 ns,
        // 否则整个文件会被当作 ns 数据、所有 key 都查不到而回退英文。
        const data = m.default as Record<string, unknown>
        const base = ((data[ns] as object | undefined) ?? data) as Record<
          string,
          unknown
        >
        callback(null, { ...base, ...getSiteOverrides(lng) })
      })
      .catch((e) => callback(e))
  },
}

// 语言 cookie 需要按部署域下发(4allapi.com / omniapi.co 共用一套构建);
// 写死主域会导致其它站点 cookie 被浏览器拒收,语言选择只能靠 localStorage。
function cookieDomain(): string {
  if (typeof window === 'undefined') return '.4allapi.com'
  const host = window.location.hostname
  const apex = host.startsWith('api.') ? host.slice(4) : host
  // localhost / IP 直接不带 domain(返回空串时 i18next 会按当前域处理)
  if (!apex.includes('.') || /^\d+\.\d+\.\d+\.\d+$/.test(apex)) return apex
  return '.' + apex
}

// init 现在是异步的(要拉当前语言的 chunk);入口在挂载前 await 它,
// 保证首帧渲染时文案已就绪,任何组件都不会 suspend/闪 key。
export const i18nReady = i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    // 'tw' 是繁体中文的内部码(见 languages.ts):真码 zh-TW 会被下面的
    // load:'languageOnly' 折叠回 zh,故用独立主码绕开;是否出现在切换器由
    // 后端 extra_interface_langs 按站点控制。
    supportedLngs: ['en', 'zh', 'fr', 'ru', 'ja', 'vi', 'ko', 'tw'],
    load: 'languageOnly', // Convert zh-CN -> zh, ko-KR -> ko
    nsSeparator: false, // Allow literal colons in keys (e.g., URLs, labels)
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    detection: {
      // querystring 优先:营销页链接沿用静态主页时代的 `?lang=xx` 约定;
      // 其后 cookie(homeLng,与静态回落页/nginx 语言分流共用)> localStorage > 系统语言。
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      caches: ['cookie', 'localStorage'],
      lookupQuerystring: 'lang',
      lookupCookie: 'homeLng',
      cookieDomain: cookieDomain(),
      cookieMinutes: 525600, // 1 year, matches homepage cookie max-age
      cookieOptions: {
        path: '/',
        sameSite: 'lax',
        secure: true, // homepage cookie is `secure`
      },
    },
  })

// 语言切换与「外部可见状态」联动:UI 文案切了但 <html lang> 和 URL ?lang
// 不跟着变,会误导搜索引擎的语言判断(html lang 是关键信号)、让读屏软件用
// 错误语音引擎朗读,also 让 hreflang 体系失真。统一在 languageChanged 里同步。
function syncLangExternals(lng: string) {
  if (typeof document === 'undefined') return
  // 内部码 tw 对外(html lang / 读屏 / 爬虫)呈现为标准 BCP47 zh-TW
  document.documentElement.lang = lng === 'tw' ? 'zh-TW' : lng
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.get('lang') !== lng) {
      url.searchParams.set('lang', lng)
      window.history.replaceState(window.history.state, '', url)
    }
  } catch {
    /* URL 不可写时忽略 */
  }
}
i18n.on('languageChanged', syncLangExternals)
void i18nReady.then(() => syncLangExternals(i18n.language))

export default i18n
